import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, ChevronDown } from 'lucide-react';

const CONSENT_KEY = 'safekept_cookie_consent';
const CONSENT_VERSION = '1'; // Bump this to re-ask if policy changes

/*
 * GDPR Cookie Consent Banner for SafeKept
 *
 * Cookie categories:
 * - strictly_necessary: session cookies, auth tokens – ALWAYS on, no consent needed
 * - analytics: PostHog – requires consent
 * - functionality: dark mode preference, UI settings – requires consent
 *
 * Consent is stored in localStorage under 'safekept_cookie_consent'.
 * Format: { version: '1', analytics: true|false, functionality: true|false, timestamp: ISO }
 *
 * ICO UK GDPR compliance:
 * - Strictly necessary cookies are exempt from consent requirement
 * - Non-essential cookies are blocked until consent is given
 * - User can change preferences at any time via /cookie-policy page
 * - Consent record includes timestamp and version for audit purposes
 */

export function getCookieConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed.version !== CONSENT_VERSION) return null; // Re-ask if version changed
    return parsed;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  const consent = getCookieConsent();
  return consent ? consent.analytics === true : false;
}

export function hasFunctionalityConsent() {
  const consent = getCookieConsent();
  return consent ? consent.functionality === true : false;
}

function saveConsent({ analytics, functionality }) {
  const record = {
    version: CONSENT_VERSION,
    analytics,
    functionality,
    timestamp: new Date().toISOString()
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  return record;
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);
  const [functionalityChecked, setFunctionalityChecked] = useState(true);

  useEffect(() => {
    // Show banner only if no valid consent record exists
    const existing = getCookieConsent();
    if (!existing) {
      // Small delay so the page loads first – avoids layout shift on first paint
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, functionality: true });
    setVisible(false);
    // If PostHog or other analytics scripts need to be initialised after consent,
    // dispatch a custom event so they can listen for it
    window.dispatchEvent(new CustomEvent('safekept_consent_granted', {
      detail: { analytics: true, functionality: true }
    }));
  };

  const handleDeclineNonEssential = () => {
    saveConsent({ analytics: false, functionality: false });
    setVisible(false);
    window.dispatchEvent(new CustomEvent('safekept_consent_granted', {
      detail: { analytics: false, functionality: false }
    }));
  };

  const handleSavePreferences = () => {
    saveConsent({ analytics: analyticsChecked, functionality: functionalityChecked });
    setVisible(false);
    window.dispatchEvent(new CustomEvent('safekept_consent_granted', {
      detail: { analytics: analyticsChecked, functionality: functionalityChecked }
    }));
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto bg-[#0F172A] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Main banner */}
        <div className="p-5 md:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 bg-teal-600/20 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
              <Cookie className="w-4 h-4 text-teal-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white text-sm mb-1">We use cookies</p>
              <p className="text-slate-400 text-xs leading-relaxed">
                SafeKept uses strictly necessary cookies to keep your account secure and the service running.
                We also use optional analytics cookies (PostHog) to understand how the platform is used – only
                with your consent.{' '}
                <Link
                  to="/cookie-policy"
                  className="text-teal-400 hover:text-teal-300 underline"
                  onClick={() => setVisible(false)}
                >
                  Cookie Policy
                </Link>
                {' '}·{' '}
                <Link
                  to="/privacy-policy"
                  className="text-teal-400 hover:text-teal-300 underline"
                  onClick={() => setVisible(false)}
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>

          {/* Preferences toggle */}
          {showPreferences && (
            <div className="mb-4 bg-white/5 rounded-xl p-4 space-y-3 border border-white/5">
              {/* Strictly necessary – always on */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-xs font-medium">Strictly Necessary</p>
                  <p className="text-slate-500 text-xs mt-0.5">Session, authentication, security. Required for the service to function.</p>
                </div>
                <div className="text-xs text-slate-500 font-medium bg-white/5 px-2.5 py-1 rounded-lg">Always on</div>
              </div>
              {/* Functionality */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-xs font-medium">Functionality</p>
                  <p className="text-slate-500 text-xs mt-0.5">Dark mode preference, display settings, notification preferences.</p>
                </div>
                <button
                  onClick={() => setFunctionalityChecked(v => !v)}
                  className={`relative w-10 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 ${functionalityChecked ? 'bg-teal-600' : 'bg-slate-600'}`}
                  role="switch"
                  aria-checked={functionalityChecked}
                  aria-label="Toggle functionality cookies"
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${functionalityChecked ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              {/* Analytics */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-xs font-medium">Analytics (PostHog)</p>
                  <p className="text-slate-500 text-xs mt-0.5">Privacy-first analytics to understand how the platform is used. No advertising.</p>
                </div>
                <button
                  onClick={() => setAnalyticsChecked(v => !v)}
                  className={`relative w-10 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 ${analyticsChecked ? 'bg-teal-600' : 'bg-slate-600'}`}
                  role="switch"
                  aria-checked={analyticsChecked}
                  aria-label="Toggle analytics cookies"
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${analyticsChecked ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              onClick={handleAcceptAll}
              className="flex-1 sm:flex-none bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold px-5 py-2.5 rounded-xl transition-colors"
              data-testid="cookie-accept-all"
            >
              Accept all
            </button>
            <button
              onClick={handleDeclineNonEssential}
              className="flex-1 sm:flex-none bg-white/10 hover:bg-white/15 text-slate-300 text-xs font-medium px-5 py-2.5 rounded-xl transition-colors border border-white/10"
              data-testid="cookie-decline"
            >
              Decline non-essential
            </button>
            {showPreferences ? (
              <button
                onClick={handleSavePreferences}
                className="flex-1 sm:flex-none bg-white/10 hover:bg-white/15 text-teal-400 text-xs font-medium px-5 py-2.5 rounded-xl transition-colors border border-teal-600/30"
                data-testid="cookie-save-preferences"
              >
                Save preferences
              </button>
            ) : (
              <button
                onClick={() => setShowPreferences(true)}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 text-slate-400 text-xs font-medium px-5 py-2.5 rounded-xl transition-colors border border-white/5"
                data-testid="cookie-manage-preferences"
              >
                Manage preferences
                <ChevronDown className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
